import { useEffect, useRef, useState } from "react";
import {
  describeSyntaxProblem,
  friendlyMessage,
  parseJson,
} from "./json-import/diagnostics";
import { Upload, FileText, AlertTriangle } from "lucide-react";
import {
  parseProject,
  type Project,
  type ImportReport,
  type ImportResult,
} from "@proof/core";
import { Button } from "./components";
import { ImportProgress } from "./ImportProgress";
import { unzipSync } from "fflate";
import {
  ProjectArchiveImport,
  type ProjectArchiveImportServices,
} from "./ProjectArchiveImport";
export interface DocumentImportSelection {
  packageContent: "native" | "edited-idml";
}
export interface DocumentImportServices {
  archive?: ProjectArchiveImportServices;
  prepareProject?(project: Project, signal: AbortSignal): Promise<Project>;
  importDocument(
    file: File,
    selection: DocumentImportSelection,
    signal: AbortSignal,
  ): Promise<ImportResult>;
}
export interface DocumentImportProps {
  onArchiveImport?: (
    project: Project,
    report: ImportReport,
    signal: AbortSignal,
  ) => void | Promise<void>;
  onArchiveOpen?: () => void;
  services: DocumentImportServices;
  onImport(
    project: Project,
    report: ImportReport | undefined,
    signal: AbortSignal,
  ): void | Promise<void>;
  title?: string;
  jsonTenant?: string;
  onOpen?: () => void;
  onReportDownload?: (report: ImportReport) => void;
}
export function downloadImportReport(report: ImportReport) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(report, null, 2)], { type: "application/json" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download =
    (report.filename.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 100) ||
      "document") + ".import-report.json";
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
/** Report metadata is host data, so reject shapes that cannot be displayed safely. */
export function snapshotImportReport(report: ImportReport): ImportReport {
  if (
    !report ||
    typeof report.filename !== "string" ||
    !Number.isInteger(report.pages) ||
    report.pages < 1 ||
    (report.variables !== undefined &&
      (!Number.isInteger(report.variables) || report.variables < 0)) ||
    !Array.isArray(report.items) ||
    !Array.isArray(report.diagnostics) ||
    report.items.some(
      (item) =>
        !item ||
        typeof item.nodeId !== "string" ||
        typeof item.name !== "string" ||
        typeof item.kind !== "string" ||
        (item.variableKey !== undefined &&
          typeof item.variableKey !== "string") ||
        (item.variableKeys !== undefined &&
          (!Array.isArray(item.variableKeys) ||
            item.variableKeys.some((key) => typeof key !== "string"))) ||
        (item.namingBasis !== undefined &&
          typeof item.namingBasis !== "string") ||
        (item.namingConfidence !== undefined &&
          typeof item.namingConfidence !== "string"),
    ) ||
    report.diagnostics.some(
      (note) => !note || typeof note.message !== "string",
    ) ||
    (report.reconciliation !== undefined &&
      (!report.reconciliation ||
        !["packaged", "regenerated"].includes(report.reconciliation.baseline) ||
        !Number.isInteger(report.reconciliation.changes) ||
        !Number.isInteger(report.reconciliation.sourceRevision)))
  )
    throw new Error("The host returned an invalid import report.");
  return structuredClone(report);
}
export function DocumentImport({
  services,
  onImport,
  title = "Import document",
  jsonTenant,
  onOpen,
  onReportDownload = downloadImportReport,
  onArchiveImport,
  onArchiveOpen,
}: DocumentImportProps) {
  const [archiveMode, setArchiveMode] = useState(false),
    [archiveFile, setArchiveFile] = useState<File>();
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [report, setReport] = useState<ImportReport>(),
    [completedJson, setCompletedJson] = useState(false),
    [filename, setFilename] = useState("");
  const [packageContent, setPackageContent] = useState<
    "native" | "edited-idml"
  >("native");
  const abort = useRef<AbortController>(undefined),
    preflight = useRef(0),
    latest = useRef({ services, jsonTenant, onImport });
  latest.current = { services, jsonTenant, onImport };
  useEffect(() => {
    setArchiveMode(false);
    setArchiveFile(undefined);
    setReport(undefined);
    setCompletedJson(false);
    setError("");
    setBusy(false);
    setFilename("");
    return () => {
      preflight.current++;
      abort.current?.abort();
      abort.current = undefined;
    };
  }, [services, jsonTenant]);
  const importFile = async (file: File) => {
    const selection = ++preflight.current;
    abort.current?.abort();
    setFilename(file.name);
    setBusy(true);
    if (
      services.archive &&
      packageContent === "native" &&
      /\.zip$/i.test(file.name)
    ) {
      let collection = file.size > 50000000;
      if (!collection) {
        try {
          const names: string[] = [];
          unzipSync(new Uint8Array(await file.arrayBuffer()), {
            filter(entry) {
              if (names.length > 100000)
                throw new Error("Archive directory limit");
              names.push(
                entry.name.replace(/\\/g, "/").replace(/^(?:\.\/)+/, ""),
              );
              return false;
            },
          });
          if (
            selection !== preflight.current ||
            latest.current.services !== services ||
            latest.current.jsonTenant !== jsonTenant
          )
            return;
          const definitions = names.filter(
            (name) =>
              /\.pf$/i.test(name) &&
              !/(?:^|\/)(?:backup|backups|autogen|__macosx)\//i.test(name),
          );
          collection =
            !names.includes("proof-package.json") &&
            (definitions.length !== 1 ||
              names.some((name) => /(?:^|\/)proof-package\.json$/i.test(name)));
        } catch {}
      }
      if (
        selection !== preflight.current ||
        latest.current.services !== services ||
        latest.current.jsonTenant !== jsonTenant
      )
        return;
      if (collection) {
        setBusy(false);
        setArchiveFile(file);
        setArchiveMode(true);
        return;
      }
    }
    abort.current?.abort();
    setError("");
    setReport(undefined);
    setCompletedJson(false);
    setFilename(file.name);
    if (file.size > 50000000) {
      setBusy(false);
      setError("Document exceeds the 50 MB import limit.");
      return;
    }
    setBusy(true);
    const controller = new AbortController();
    abort.current = controller;
    const current = () =>
      !controller.signal.aborted &&
      abort.current === controller &&
      latest.current.services === services &&
      latest.current.jsonTenant === jsonTenant;
    try {
      let project: Project, importReport: ImportReport | undefined;
      if (
        packageContent === "native" &&
        jsonTenant &&
        /\.json$/i.test(file.name)
      )
      {
        const parsed = parseJson(await file.text());
        if (!parsed.ok)
          throw new Error(
            `${file.name} isn't valid JSON. ${describeSyntaxProblem(parsed.problem)}`,
          );
        if (!parsed.value || typeof parsed.value !== "object" || Array.isArray(parsed.value))
          throw new Error(`${file.name} isn't a Proof Studio project file.`);
        project = parseProject({
          ...(parsed.value as object),
          tenantId: jsonTenant,
        });
      }
      else {
        const result = await services.importDocument(
          file,
          { packageContent },
          controller.signal,
        );
        if (!current()) return;
        project = parseProject(structuredClone(result.project));
        if (jsonTenant && project.tenantId !== jsonTenant)
          throw new Error(
            "The imported project belongs to a different tenant.",
          );
        importReport = snapshotImportReport(result.report);
      }
      if (!current()) return;
      if (services.prepareProject) {
        project = parseProject(
          await services.prepareProject(project, controller.signal),
        );
        if (!current()) return;
        if (jsonTenant && project.tenantId !== jsonTenant)
          throw new Error(
            "The imported project belongs to a different tenant.",
          );
      }
      await latest.current.onImport(
        structuredClone(project),
        importReport && structuredClone(importReport),
        controller.signal,
      );
      if (current()) {
        setReport(importReport);
        setCompletedJson(!importReport);
      }
    } catch (error) {
      if (current())
        setError(
          error instanceof Error || typeof error === "string"
            ? friendlyMessage(error)
            : "Import failed",
        );
    } finally {
      if (current()) {
        setBusy(false);
        abort.current = undefined;
      }
    }
  };
  return (
    <section aria-label={title}>
      {services.archive && (
        <div className="button-row">
          <Button
            disabled={busy}
            onClick={() => {
              setArchiveFile(undefined);
              setArchiveMode(false);
            }}
          >
            One document
          </Button>
          <Button disabled={busy} onClick={() => setArchiveMode(true)}>
            Project archive (ZIP)
          </Button>
        </div>
      )}
      {archiveMode && services.archive ? (
        <ProjectArchiveImport
          services={services.archive}
          initialFile={archiveFile}
          jsonTenant={jsonTenant}
          onImport={onArchiveImport || onImport}
          onOpen={onArchiveOpen || onOpen}
        />
      ) : (
        <>
          <p>
            Turn existing artwork into editable layers. Explicit text
            placeholders become shared variables, with each placement retaining
            its formatting.
          </p>
          {!report && (
            <label className="field">
              <span>Package content</span>
              <select
                aria-label="Package content"
                value={packageContent}
                disabled={busy}
                onChange={(event) => {
                  setPackageContent(
                    event.target.value as "native" | "edited-idml",
                  );
                  setError("");
                }}
              >
                <option value="native">
                  Original artwork or native snapshot
                </option>
                <option value="edited-idml">Edited IDML project ZIP</option>
              </select>
              <small>
                {packageContent === "edited-idml"
                  ? "Upload the complete exported ZIP with edited Target artwork and links. The native snapshot, resources and baseline must be unchanged. Changes are proposed for review."
                  : "Import artwork or restore the native snapshot from an unchanged portable ZIP."}
              </small>
            </label>
          )}
          {!report && (
            <label className="document-import-drop">
              <Upload size={28} />
              <strong>
                {busy
                  ? `Importing ${filename}…`
                  : packageContent === "edited-idml"
                    ? "Choose edited project ZIP"
                    : `Choose PDF, IDML, INDD, portable or publishing ZIP, or image${jsonTenant ? ", or project JSON" : ""}`}
              </strong>
              <span>
                {packageContent === "edited-idml"
                  ? "Up to 50 MB · Complete exported project ZIP"
                  : "Up to 50 MB · INDT templates also supported"}
              </span>
              {packageContent === "native" && (
                <span>
                  Publishing ZIP import is in preview. Review layout warnings
                  before use.
                </span>
              )}
              <input
                key={packageContent}
                aria-label="Document file"
                type="file"
                accept={
                  packageContent === "edited-idml"
                    ? ".zip"
                    : `.pdf,.idml,.indd,.indt,.zip,.png,.jpg,.jpeg,.webp,.tif,.tiff${jsonTenant ? ",.json" : ""}`
                }
                disabled={busy}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) void importFile(file);
                }}
              />
            </label>
          )}
          {busy && (
            <ImportProgress onCancel={() => {
              preflight.current++;
              abort.current?.abort();
              abort.current = undefined;
              setBusy(false);
            }}>
              <p>{filename}</p>
              <p>Extracting pages, content, and editable variables…</p>
              <progress aria-label="Document import progress" />
            </ImportProgress>
          )}
          {error && (
            <p className="error-text" role="alert">
              {error}
            </p>
          )}
          {completedJson && (
            <div>
              <p role="status">Imported project JSON.</p>
              {onOpen && (
                <Button kind="primary" onClick={onOpen}>
                  Open in designer
                </Button>
              )}
            </div>
          )}
          {report && (
            <>
              <div className="document-import-result">
                <FileText size={24} />
                <div>
                  <h3>{report.filename}</h3>
                  <p role="status">
                    Created {report.pages}{" "}
                    {report.pages === 1 ? "page" : "pages"} and{" "}
                    {report.variables ?? report.items.length} editable
                    variables.
                  </p>
                  <p>
                    High Res and Print include detected page production
                    settings. Proof Download omits bleed, slug and page marks.
                  </p>
                </div>
              </div>
              <div className="document-import-items">
                <table>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Variable</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map((item, i) => (
                      <tr key={item.nodeId}>
                        <td>{item.name}</td>
                        <td>
                          {item.variableKeys?.join(", ") ||
                            item.variableKey ||
                            `Item${String(i + 1).padStart(4, "0")}`}
                          <small>
                            {item.namingBasis} · {item.namingConfidence}{" "}
                            confidence
                          </small>
                        </td>
                        <td>{item.kind}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!!report.diagnostics.length && (
                <div className="document-import-warnings">
                  <h3>
                    <AlertTriangle size={16} /> Review{" "}
                    {report.diagnostics.length} import notes
                  </h3>
                  <ul>
                    {report.diagnostics.map((note, i) => (
                      <li key={i}>{note.message}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="button-row">
                <Button
                  onClick={() => onReportDownload(structuredClone(report))}
                >
                  Download import report
                </Button>
                {onOpen && (
                  <Button kind="primary" onClick={onOpen}>
                    Open in designer
                  </Button>
                )}
              </div>
            </>
          )}
          <p className="muted">
            {packageContent === "edited-idml" ? (
              "The native snapshot supplies forms, rules and settings. Review target changes and any differences from your current draft before saving."
            ) : (
              <>
                PDF text runs become individual fields; outlined text stays
                vector artwork. IDML/INDD linked images must be embedded or
                replaced in the generated form. Verify fonts and appearance
                using the import notes.
              </>
            )}
          </p>
        </>
      )}
    </section>
  );
}
